import React, { useEffect, useMemo, useState } from "react";

import { Box, CircularProgress } from "@mui/material";

import type { Template } from "@safetyculture/s12-apis-connect-web/s12/templates/v1/template_pb";

import {
  GetProductPreviewParams,
  getProductPreview,
} from "api/endpoints/content-library";
import { InspectionIframe } from "components/search/components/content-preview/inspection-iframe";
import useStyles from "components/search/styles";

interface ContentPreviewProps {
  id: string;
  publisherOrgId: string;
}

export function ContentPreview({ id, publisherOrgId }: ContentPreviewProps) {
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const [error, setError] = useState(false);
  const [template, setTemplate] = useState<Template>();
  const clStyle = useStyles();

  useEffect(() => {
    const getTemplate = async () => {
      const getProductPreviewParams: GetProductPreviewParams = {
        productId: id,
        publisherOrgId,
      };

      getProductPreview(getProductPreviewParams)
        .then((resp) => {
          if (resp.data.case == "templateData") {
            const templateData = resp.data.value as Template;
            setTemplate(templateData);
            setIsIframeLoading(false);
            setError(false);
          } else {
            setIsIframeLoading(false);
            setError(true);
          }
        })
        .catch(() => {
          setIsIframeLoading(false);
          setError(true);
        });
    };

    setError(false);
    setTemplate(undefined);
    setIsIframeLoading(true);
    getTemplate();
  }, [id, publisherOrgId]);

  const iframe = useMemo(
    () =>
      template ? (
        <InspectionIframe
          onError={() => {
            setIsIframeLoading(false);
            setError(true);
          }}
          onReady={() => {
            setIsIframeLoading(false);
            setError(false);
          }}
          template={template}
        />
      ) : null,
    [template],
  );
  if (error) {
    return (
      <Box paddingTop={2}>
        <span className="prose-title-large">Failed to load preview</span>
      </Box>
    );
  } else if (isIframeLoading) {
    return (
      <Box paddingTop={2}>
        <div>Loading</div>
        <CircularProgress
          sx={clStyle.absoluteSpinner}
          color="primary"
          variant="indeterminate"
          thickness={3}
          size={30}
        />
      </Box>
    );
  }
  return (
    <Box component="section" sx={clStyle.previewFrame}>
      {iframe}
    </Box>
  );
}
